import { getStateAfterValidation } from 'components/Forms/Helpers/getStateAfterValidation';
import LabelWrapper from 'components/Forms/Lib/LabelWrapper/LabelWrapper';
import { TextareaHTMLAttributes, useEffect, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { twJoin } from 'tailwind-merge';
import { ExtractNativePropsFromDefault } from 'typeHelpers/ExtractNativePropsFromDefault';

type NativeProps = ExtractNativePropsFromDefault<
    TextareaHTMLAttributes<HTMLTextAreaElement>,
    'rows',
    'disabled' | 'required' | 'name' | 'id' | 'style' | 'onBlur'
>;

type TextareaProps = NativeProps & {
    label: string;
    hasError: boolean;
    isTouched: boolean;
    markSuccessfulWhenValid?: boolean;
    fieldRef?: ControllerRenderProps<any, any>;
};

const Textarea: FC<TextareaProps> = ({
    label,
    hasError,
    isTouched,
    id,
    markSuccessfulWhenValid,
    fieldRef,
    ...props
}) => {
    const [inputState, setInputState] = useState<'success' | 'error' | undefined>(undefined);

    useEffect(() => {
        setInputState(getStateAfterValidation(hasError, isTouched, markSuccessfulWhenValid));
    }, [hasError, isTouched, markSuccessfulWhenValid]);

    const isEmpty = fieldRef?.value === undefined || fieldRef.value === '';

    return (
        <LabelWrapper htmlFor={id} inputType="textarea" isEmpty={isEmpty} label={label} required={props.required}>
            <textarea
                id={id}
                className={twJoin(
                    'peer box-border w-full resize-y rounded-lg border border-solid border-border bg-white px-2 py-5 text-body text-primary w-appearance-none',
                    inputState === 'error' && 'border-redIntense bg-white shadow-redShadow',
                    inputState === 'success' && 'rounded-md border-green shadow-green',
                    'focus:border-borderFocus focus:outline focus:outline-[3px] focus:outline-outlineFocus',
                    'placeholder:text-transparent',
                    'disabled/read-only:pointer-events-none disabled/read-only:cursor-no-drop disabled/read-only:opacity-50',
                    'webkit-autofill:!bg-white webkit-autofill:!shadow-input webkit-autofill:hover:!bg-white webkit-autofill:hover:!shadow-input webkit-autofill:focus:!bg-white webkit-autofill:focus:!shadow-input internal-autofill-selected:!bg-white internal-autofill-selected:!shadow-input',
                )}
                {...fieldRef}
                {...props}
                placeholder={label}
            />
        </LabelWrapper>
    );
};

/* @component */
export default Textarea;
